import { useMemo, useEffect } from 'react';
import { normalizeRole, normalizeRegion } from '../../../utils/assignmentRules';
import { getUserDisplayName } from '../../../utils/escalateQueryUtils';
import { ESCALATION_FIELDS_BY_ROLE, ROLE_LABELS } from '../../../constants/escalateQueryDialog';

/**
 * Custom hook for escalation query calculations and auto-selection
 * @param {Object} currentUser - Current user object
 * @param {Array} allowedRoles - Allowed roles array
 * @param {Array} targets - Target users array
 * @param {string} selectedRole - Selected role
 * @param {string} selectedUserId - Selected user ID
 * @param {Object} stateSetters - Object with state setters
 * @returns {Object} Computed values
 */
export const useEscalateQueryCalculations = (
  currentUser,
  allowedRoles,
  targets,
  selectedRole,
  selectedUserId,
  stateSetters
) => {
  const { setSelectedRole, setSelectedUserId, setErrors } = stateSetters;

  const normalizedRole = useMemo(
    () => normalizeRole(currentUser?.role),
    [currentUser]
  );

  const fields = useMemo(() => {
    if (!normalizedRole) return [];
    const roleFields = ESCALATION_FIELDS_BY_ROLE[normalizedRole] || [];
    if (!allowedRoles || allowedRoles.length === 0) {
      return roleFields;
    }
    const allowedSet = new Set(allowedRoles.map((role) => normalizeRole(role)));
    return roleFields.filter((field) => allowedSet.has(field.role));
  }, [normalizedRole, allowedRoles]);

  const usersById = useMemo(() => {
    const map = new Map();
    targets.forEach((user) => {
      const rawId = user?.id || user?._id;
      if (rawId) {
        map.set(rawId.toString(), user);
      }
    });
    return map;
  }, [targets]);

  const computedRoleOptions = useMemo(() => {
    if (!fields.length) return [];
    const allowedSet = new Set(fields.map((field) => normalizeRole(field.role)));
    const grouped = new Map();

    targets.forEach((user) => {
      const roleKey = normalizeRole(user?.role);
      if (!allowedSet.has(roleKey)) return;
      const id = user?._id || user?.id;
      if (!id) return;
      const existing =
        grouped.get(roleKey) ||
        {
          role: roleKey,
          roleLabel: ROLE_LABELS[roleKey] || roleKey,
          users: [],
        };
      existing.users.push({
        id: id.toString(),
        name: getUserDisplayName(user),
        region: normalizeRegion(user?.region) || 'ALL',
      });
      grouped.set(roleKey, existing);
    });

    return Array.from(grouped.values())
      .map((entry) => ({
        ...entry,
        users: entry.users.sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        ),
      }))
      .sort((a, b) =>
        (a.roleLabel || '').localeCompare(b.roleLabel || '', undefined, { sensitivity: 'base' })
      );
  }, [fields, targets]);

  // Auto-select role if only one option
  useEffect(() => {
    if (computedRoleOptions.length === 1) {
      const onlyRole = computedRoleOptions[0];
      if (onlyRole && selectedRole !== onlyRole.role) {
        setSelectedRole(onlyRole.role);
        setErrors((prev) => ({ ...prev, role: undefined }));
      }
    } else if (selectedRole && !computedRoleOptions.some((option) => option.role === selectedRole)) {
      setSelectedRole('');
      setSelectedUserId('');
    }
  }, [computedRoleOptions, selectedRole, setSelectedRole, setSelectedUserId, setErrors]);

  // Auto-select user if only one option for selected role
  useEffect(() => {
    if (!selectedRole) {
      setSelectedUserId('');
      return;
    }
    const roleEntry = computedRoleOptions.find((option) => option.role === selectedRole);
    if (!roleEntry) {
      setSelectedUserId('');
      return;
    }
    if (roleEntry.users.length === 1) {
      const onlyUser = roleEntry.users[0];
      if (onlyUser && selectedUserId !== onlyUser.id) {
        setSelectedUserId(onlyUser.id);
        setErrors((prev) => ({ ...prev, target: undefined }));
      }
    } else if (selectedUserId && !roleEntry.users.some((user) => user.id === selectedUserId)) {
      setSelectedUserId('');
    }
  }, [computedRoleOptions, selectedRole, selectedUserId, setSelectedUserId, setErrors]);

  return {
    normalizedRole,
    fields,
    usersById,
    roleOptions: computedRoleOptions,
  };
};
